import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { cn, formatDate, formatDateOnly, formatCurrency } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { PageHeader, BackLink } from '@/layouts/AppLayout';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Client {
  id: string;
  name: string;
  type: 'travel_agent' | 'b2c' | 'corporate';
  contactPerson: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  address: string | null;
  city: string | null;
  country: string;
  gstin: string | null;
  pan: string | null;
  creditDays: number;
  status: 'active' | 'inactive';
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
}

interface Booking {
  id: string;
  bookingCode: string;
  status: string;
  guestName: string;
  checkIn: string;
  checkOut: string;
  rooms: number;
  amount: string;
  hotel: { id: string; name: string };
}

interface Rate {
  id: string;
  hotelRoomType: { id: string; name: string };
  mealPlan: string;
  occupancy: string;
  rate: string;
  currency: string;
  validFrom: string;
  validTo: string;
  hotel: { id: string; name: string };
}

interface AuditEntry {
  id: string;
  action: string;
  at: string;
  user: { id: string; fullName: string; email: string } | null;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<Client['type'], string> = {
  travel_agent: 'Travel Agent',
  b2c: 'B2C',
  corporate: 'Corporate',
};

const BOOKING_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  tentative: 'Tentative',
  confirmed: 'Confirmed',
  voucher_issued: 'Voucher Issued',
  paid: 'Paid',
  completed: 'Completed',
  released: 'Released',
  cancelled: 'Cancelled',
};

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'bookings', label: 'Bookings' },
  { id: 'rates', label: 'Rates' },
  { id: 'audit', label: 'Audit log' },
] as const;

type TabId = (typeof TABS)[number]['id'];

// ── Sub-components ────────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="flex gap-4 py-2.5 border-b border-neutral-100 last:border-0">
      <span className="w-40 shrink-0 text-sm text-neutral-500">{label}</span>
      <span className="text-sm text-neutral-900">{value ?? '—'}</span>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white">
      <div className="px-4 py-3 border-b border-neutral-100">
        <h3 className="text-sm font-semibold text-neutral-700">{title}</h3>
      </div>
      <div className="px-4 py-1">{children}</div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-neutral-300 py-12 text-center text-sm text-neutral-500">
      {message}
    </div>
  );
}

function ErrorState({ error }: { error: unknown }) {
  const msg =
    error instanceof ApiError && error.status === 403
      ? "You don't have permission to view this data."
      : 'Failed to load data.';
  return (
    <div className="rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-700">{msg}</div>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

function OverviewTab({ client }: { client: Client }) {
  return (
    <div className="space-y-4">
      <SectionCard title="Contact details">
        <InfoRow label="Contact person" value={client.contactPerson} />
        <InfoRow label="Email" value={client.contactEmail} />
        <InfoRow label="Phone" value={client.contactPhone} />
        <InfoRow label="Address" value={client.address} />
        <InfoRow label="City" value={client.city} />
        <InfoRow label="Country" value={client.country} />
      </SectionCard>

      <SectionCard title="Billing & compliance">
        <InfoRow label="GSTIN" value={client.gstin} />
        <InfoRow label="PAN" value={client.pan} />
        <InfoRow label="Credit days" value={client.creditDays > 0 ? `${client.creditDays} days` : '0 (no credit)'} />
      </SectionCard>

      {client.notes && (
        <SectionCard title="Notes">
          <p className="py-3 text-sm text-neutral-700 whitespace-pre-wrap">{client.notes}</p>
        </SectionCard>
      )}

      <p className="text-xs text-neutral-400">
        Created {formatDate(client.createdAt)}
        {client.updatedAt && ` · Updated ${formatDate(client.updatedAt)}`}
      </p>
    </div>
  );
}

function BookingsTab({ clientId }: { clientId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['clients', clientId, 'bookings'],
    queryFn: () => api.get<Booking[]>(`/clients/${clientId}/bookings`),
  });

  if (isLoading) return <div className="flex justify-center py-10"><Spinner className="h-5 w-5" /></div>;
  if (error) return <ErrorState error={error} />;
  if (!data?.length) return <EmptyState message="No bookings for this client yet." />;

  return (
    <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 bg-neutral-50">
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Booking</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Hotel</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Guest</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Dates</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Status</th>
            <th className="px-4 py-3 text-right font-medium text-neutral-500">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {data.map((b) => (
            <tr key={b.id} className="hover:bg-neutral-50 transition-colors">
              <td className="px-4 py-3">
                <Link to={`/bookings/${b.id}`} className="font-medium text-neutral-900 hover:text-tangerine-600">
                  {b.bookingCode}
                </Link>
              </td>
              <td className="px-4 py-3 text-neutral-700">{b.hotel.name}</td>
              <td className="px-4 py-3 text-neutral-600">{b.guestName}</td>
              <td className="px-4 py-3 text-neutral-600 text-xs">
                {formatDateOnly(b.checkIn)} → {formatDateOnly(b.checkOut)}
                <div className="text-neutral-400">{b.rooms} room{b.rooms !== 1 ? 's' : ''}</div>
              </td>
              <td className="px-4 py-3">
                <Badge variant={b.status as Parameters<typeof Badge>[0]['variant']}>
                  {BOOKING_STATUS_LABELS[b.status] ?? b.status}
                </Badge>
              </td>
              <td className="px-4 py-3 text-right font-medium text-neutral-900">
                {formatCurrency(b.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RatesTab({ clientId }: { clientId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['clients', clientId, 'rates'],
    queryFn: () => api.get<Rate[]>(`/clients/${clientId}/rates`),
  });

  if (isLoading) return <div className="flex justify-center py-10"><Spinner className="h-5 w-5" /></div>;
  if (error) return <ErrorState error={error} />;
  if (!data?.length) return <EmptyState message="No contracted rates for this client yet." />;

  return (
    <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 bg-neutral-50">
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Hotel</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Room type</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Meal plan</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Occupancy</th>
            <th className="px-4 py-3 text-right font-medium text-neutral-500">Rate/night</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Valid</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {data.map((r) => (
            <tr key={r.id} className="hover:bg-neutral-50 transition-colors">
              <td className="px-4 py-3">
                <Link to={`/hotels/${r.hotel.id}`} className="font-medium text-neutral-900 hover:text-tangerine-600">
                  {r.hotel.name}
                </Link>
              </td>
              <td className="px-4 py-3 text-neutral-700">{r.hotelRoomType?.name}</td>
              <td className="px-4 py-3 text-neutral-600">{r.mealPlan.toUpperCase()}</td>
              <td className="px-4 py-3 text-neutral-600 capitalize">{r.occupancy}</td>
              <td className="px-4 py-3 text-right font-medium text-neutral-900">
                {formatCurrency(r.rate)}
              </td>
              <td className="px-4 py-3 text-xs text-neutral-500">
                {formatDateOnly(r.validFrom)} – {formatDateOnly(r.validTo)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ACTION_LABELS: Record<string, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
};

function AuditTab({ clientId }: { clientId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['clients', clientId, 'audit'],
    queryFn: () => api.get<AuditEntry[]>(`/clients/${clientId}/audit`),
  });

  if (isLoading) return <div className="flex justify-center py-10"><Spinner className="h-5 w-5" /></div>;
  if (error) return <ErrorState error={error} />;
  if (!data?.length) return <EmptyState message="No audit history for this client." />;

  return (
    <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 bg-neutral-50">
            <th className="px-4 py-3 text-left font-medium text-neutral-500">Action</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">By</th>
            <th className="px-4 py-3 text-left font-medium text-neutral-500">When</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {data.map((entry) => (
            <tr key={entry.id} className="hover:bg-neutral-50 transition-colors">
              <td className="px-4 py-3">
                <span
                  className={cn(
                    'inline-flex items-center rounded-sm px-2 py-0.5 text-xs font-medium',
                    entry.action === 'create' && 'bg-success-50 text-success-700',
                    entry.action === 'update' && 'bg-info-50 text-info-700',
                    entry.action === 'delete' && 'bg-danger-50 text-danger-700',
                    !['create', 'update', 'delete'].includes(entry.action) && 'bg-neutral-100 text-neutral-700',
                  )}
                >
                  {ACTION_LABELS[entry.action] ?? entry.action}
                </span>
              </td>
              <td className="px-4 py-3 text-neutral-700">
                {entry.user ? (
                  <div>
                    <span>{entry.user.fullName}</span>
                    <div className="text-xs text-neutral-400">{entry.user.email}</div>
                  </div>
                ) : (
                  <span className="text-neutral-400">System</span>
                )}
              </td>
              <td className="px-4 py-3 text-neutral-500 text-xs">{formatDate(entry.at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  const { data: client, isLoading } = useQuery({
    queryKey: ['clients', id],
    queryFn: () => api.get<Client>(`/clients/${id}`),
    enabled: !!id,
  });

  if (isLoading) {
    return <div className="flex justify-center py-16"><Spinner className="h-6 w-6" /></div>;
  }
  if (!client) {
    return <p className="p-6 text-danger-700">Client not found.</p>;
  }

  return (
    <div>
      <PageHeader
        title={client.name}
        description={[client.city, client.country].filter(Boolean).join(', ')}
        action={
          <Button asChild>
            <Link to={`/clients/${id}/edit`}>Edit</Link>
          </Button>
        }
      />

      <div className="p-6 max-w-4xl">
        <BackLink to="/clients" label="Back to clients" />

        {/* Status badges */}
        <div className="flex items-center gap-2 mb-5">
          <Badge variant={client.type}>{TYPE_LABELS[client.type]}</Badge>
          <Badge variant={client.status}>{client.status === 'active' ? 'Active' : 'Inactive'}</Badge>
          {client.creditDays > 0 && (
            <span className="text-xs text-neutral-500 bg-neutral-100 rounded-sm px-2 py-0.5">
              {client.creditDays}d credit
            </span>
          )}
        </div>

        {/* Tab nav */}
        <div className="flex border-b border-neutral-200 mb-5">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.id
                  ? 'border-tangerine-500 text-tangerine-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'overview' && <OverviewTab client={client} />}
        {activeTab === 'bookings' && <BookingsTab clientId={client.id} />}
        {activeTab === 'rates' && <RatesTab clientId={client.id} />}
        {activeTab === 'audit' && <AuditTab clientId={client.id} />}
      </div>
    </div>
  );
}
